import React, {FunctionComponent, useCallback, useLayoutEffect, useState} from "react";
import {Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import ReduxStoreRoutes from "../../../core/models/static/routes/redux-store-routes";
import {setReduxDialogState} from "../../redux/slices/dialogs/actions";
import useIsMounted from "../../hooks/use-is-mounted";
import Button from "../../components/base/button/button";


const ConfirmationDialog: FunctionComponent = () => {
    const dispatch = useDispatch();
    const state = useSelector(state => state?.[ReduxStoreRoutes.dialogs.base][ReduxStoreRoutes.dialogs.confirmation] ?? {});
    const [processing, setProcessing] = useState(false);
    const isMounted = useIsMounted();

    const {hideCloseButton, disableBackdropClick, mandatory, cancelButtonText, confirmButtonText, otherButtons, ...dialogProps} = state;

    /**
     * As soon as the dialog opens, resets the state values.
     */
    useLayoutEffect(() => {
        if (state?.open) {
            setProcessing(false);
        }
    }, [state?.open])

    /**
     * Closes the dialog by dispatching its relevant action to the redux store.
     */
    const closeDialog = useCallback(async (reason: string, successful = false) => {
        if (state?.onClose) {
            setProcessing(true);
            const resultFlag = await state.onClose(successful, reason);
            if (!isMounted())
                return;
            setProcessing(false);
            if (successful && !resultFlag)
                return;
        }
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {open: false}));
    }, [state, dispatch, isMounted])

    /**
     * Closes the dialog if the values from the state do not prevent the closing of the dialog.
     */
    const onDialogClosed = useCallback((event: Object, reason: 'backdropClick' | 'escapeKeyDown' | 'closeClick' | "confirmClick" | (string & {})) => {
        switch (reason) {
            case "backdropClick":
                if (!state.disableBackdropClick && !processing)
                    void closeDialog(reason, false);
                break;
            case "closeClick":
                if (!state.hideCloseButton && !processing)
                    void closeDialog(reason, false);
                break;
            case "escapeKeyDown":
                if (!state.disableEscapeKeyDown && !processing)
                    void closeDialog(reason, false)
                break;
            case "confirmClick":
                if (!processing)
                    void closeDialog(reason, true)
                break;
            default:
                if (!processing)
                    void void closeDialog(reason);
                break;
        }
    }, [processing, closeDialog, state.disableBackdropClick, state.disableEscapeKeyDown, state.hideCloseButton])

    return (
        <>
            <Dialog
                {...dialogProps}
                onClose={onDialogClosed}
            >
                <DialogTitle fontWeight={600}>
                    {state.title ?? "Confirmation"}
                </DialogTitle>
                <DialogContent dividers className={'py-3 pb-5'}>
                    {
                        (typeof state.description === "string" || !state.description) &&
                        <DialogContentText variant={'body1'}>
                            {
                                state.description ?? "Would you like to proceed with the requested action?"
                            }
                        </DialogContentText>
                    }
                    {
                        typeof state.description !== "string" && !!state.description &&
                        state.description
                    }

                </DialogContent>
                <DialogActions sx={{display: 'flex', justifyContent: 'space-between', padding: 0}}>
                    <Button
                        variant={'dialog'}
                        type={'button'}
                        color={'primary'}
                        disabled={processing}
                        onClick={() => onDialogClosed({}, 'closeClick')}
                        // @ts-ignore
                        borderRadius={'0 8px 0 8px'}
                    >
                        {
                            state.cancelButtonText ?? "Cancel"
                        }
                    </Button>
                    <Box sx={{display: 'flex', gap: 1}}>
                        {
                            state.otherButtons?.map((button, index) => (
                                <Button
                                    key={index}
                                    variant={'dialog'}
                                    color={button.color ?? 'primary'}
                                    disabled={processing}
                                    onClick={() => onDialogClosed({}, button.reason)}
                                    // @ts-ignore
                                    borderRadius={index === 0 ? '8px 0 8px 0' : '0 0 0 0'}
                                >
                                    {button.text}
                                </Button>
                            ))
                        }
                    </Box>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        disabled={processing}
                        onClick={() => onDialogClosed({}, "confirmClick")}
                        // @ts-ignore
                        borderRadius={!state.otherButtons?.length ? '8px 0 8px 0' : '0 0 8px 0'}
                        autoFocus
                    >
                        {
                            processing
                                ? "Processing..."
                                : state.confirmButtonText ?? "Confirm"
                        }
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default ConfirmationDialog;
